import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Target, ListChecks, FileSpreadsheet, Mail, Settings, Sparkles, History, Search, Plus, MoreVertical, Shield, CheckCircle2, XCircle, ExternalLink, Save, ToggleLeft, ToggleRight } from 'lucide-react';
import { ROLES } from '../data/framework';
import { PICKS_PER_TYPE, questionnaireSize } from '../utils/framework';
import { CURRENT_ADMIN, ORGANISATION, personById } from '../data/demoOrganisation';
type AdminTab = 'users' | 'competencies' | 'questions' | 'templates' | 'emails' | 'platform' | 'ai' | 'audit';
export function Administration() {
  const [activeTab, setActiveTab] = useState<AdminTab>('users');
  const tabs = [{
    id: 'users',
    label: 'User Management',
    icon: Users
  }, {
    id: 'competencies',
    label: 'Competency Management',
    icon: Target
  }, {
    id: 'questions',
    label: 'Question Management',
    icon: ListChecks
  }, {
    id: 'templates',
    label: 'Role Questionnaires',
    icon: FileSpreadsheet
  }, {
    id: 'emails',
    label: 'Email Templates',
    icon: Mail
  }, {
    id: 'platform',
    label: 'Platform Settings',
    icon: Settings
  }, {
    id: 'ai',
    label: 'AI Settings',
    icon: Sparkles
  }, {
    id: 'audit',
    label: 'Audit Logs',
    icon: History
  }] as const;
  return <div className="space-y-6 animate-in fade-in duration-500 pb-12" data-id="element-197" data-mp="2oh24ms9bx43p">
      {/* Header */}
      <div data-id="element-198" data-mp="29s5qcdgoiuun">
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center" data-id="element-199" data-mp="29sffjytwgxza">
          <Shield className="h-6 w-6 mr-3 text-slate-700" data-id="element-200" data-mp="29qbctk7qm50l" />
          Administration
        </h1>
        <p className="text-slate-500 mt-1" data-id="element-201" data-mp="2eow5mykntmf0">
          Manage system configurations, users, and platform-wide settings.
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-6" data-id="element-202" data-mp="378l9l4mwys8d">
        {/* Left Sidebar Nav */}
        <div className="w-full md:w-64 shrink-0" data-id="element-203" data-mp="3h3s66cvl0ur5">
          <nav className="space-y-1" data-id="element-204" data-mp="3h1fpe31k8qfo">
            {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`} data-id="element-205" data-mp="3810r833hzgur">
                  <Icon className={`mr-3 h-4 w-4 ${isActive ? 'text-slate-300' : 'text-slate-400'}`} data-id="element-206" data-mp="2oel5b78nnt2q" />
                  {tab.label}
                </button>;
          })}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0" data-id="element-207" data-mp="2af3dej02bzp5">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden min-h-[500px]" data-id="element-208" data-mp="2ag4tj1vxrcdq">
            {activeTab === 'users' && <UserManagementPanel data-id="element-209" data-mp="pz5eja7ssw3o" />}
            {activeTab === 'competencies' && <CompetencyManagementPanel data-id="element-210" data-mp="uuuhlnj10v4q" />}
            {activeTab === 'questions' && <QuestionManagementPanel data-id="element-211" data-mp="zuywj94adhsu" />}
            {activeTab === 'templates' && <AssessmentTemplatesPanel data-id="element-212" data-mp="13wtvw82npl3z" />}
            {activeTab === 'emails' && <EmailTemplatesPanel data-id="element-213" data-mp="13w0yvyti29z1" />}
            {activeTab === 'platform' && <PlatformSettingsPanel data-id="element-214" data-mp="77cjzbd2z2tu" />}
            {activeTab === 'ai' && <AISettingsPanel data-id="element-215" data-mp="74qdzg5u8vdq" />}
            {activeTab === 'audit' && <AuditLogsPanel data-id="element-216" data-mp="c6fliltuqqx2" />}
          </div>
        </div>
      </div>
    </div>;
}
// --- Individual Panels ---
function UserManagementPanel() {
  return <div className="flex flex-col h-full" data-id="element-217" data-mp="kxpsbetvwjjp">
      <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50" data-id="element-218" data-mp="kziyyu4hz3rp">
        <div data-id="element-219" data-mp="3kky0l7i7n6q6">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-220" data-mp="3kjd77l7mtttw">
            User Management
          </h2>
          <p className="text-sm text-slate-500" data-id="element-221" data-mp="3okyij24iq01z">
            Manage administrators, managers, and system access.
          </p>
        </div>
        <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center" data-id="element-222" data-mp="30jv11so554lx">
          <Plus className="h-4 w-4 mr-2" data-id="element-223" data-mp="3fac41ggfc2kx" />
          Invite User
        </button>
      </div>
      <div className="p-4 border-b border-slate-200" data-id="element-224" data-mp="3ag7uqqjorigo">
        <div className="relative max-w-md" data-id="element-225" data-mp="3o8e8k5zd9dcy">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-226" data-mp="3o72y5nbf17cv" />
          <input type="text" placeholder="Search users by name or email..." className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm focus:bg-white focus:border-slate-300 outline-none" data-id="element-227" data-mp="3j91f32dzp81e" />
        </div>
      </div>
      <div className="flex-1 overflow-auto" data-id="element-228" data-mp="23u3w0zi83z1v">
        <table className="w-full text-sm text-left" data-id="element-229" data-mp="23ux8mh3v5irx">
          <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-230" data-mp="1yv12ocblvh9q">
            <tr data-id="element-231" data-mp="ed03nqoh545t">
              <th className="px-6 py-3 font-medium" data-id="element-232" data-mp="ecgqen9rr3y4">User</th>
              <th className="px-6 py-3 font-medium" data-id="element-233" data-mp="jbb9zrfk8wja">Role</th>
              <th className="px-6 py-3 font-medium" data-id="element-234" data-mp="jcurtmgv7fn6">Status</th>
              <th className="px-6 py-3 font-medium text-right" data-id="element-235" data-mp="4lcfpzr4m593">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-236" data-mp="9jnpyblc2hru">
            {[{
            name: CURRENT_ADMIN.name,
            email: CURRENT_ADMIN.email,
            role: 'Subscriber Admin',
            status: 'Active'
          }, {
            name: personById('p1').name,
            email: personById('p1').email,
            role: 'Board Member',
            status: 'Active'
          }, {
            name: personById('p2').name,
            email: personById('p2').email,
            role: 'Executive',
            status: 'Active'
          }, {
            name: personById('p12').name,
            email: personById('p12').email,
            role: 'Board Member',
            status: 'Pending'
          }].map((user, i) => <tr key={i} className="hover:bg-slate-50" data-id="element-237" data-mp="19ltxk78s8869" data-mpi={i}>
                <td className="px-6 py-4" data-id="element-238" data-mp="14m7jxwp1kpwx">
                  <div className="font-medium text-slate-900" data-id="element-239" data-mp="14nijtlfd9dir">{user.name}</div>
                  <div className="text-slate-500" data-id="element-240" data-mp="228ob0k76m7a8">{user.email}</div>
                </td>
                <td className="px-6 py-4" data-id="element-241" data-mp="1xad1kakstysj">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800" data-id="element-242" data-mp="1xbenxb79m7pk">
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4" data-id="element-243" data-mp="1niz7wjfj3t7b">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.status === 'Active' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-244" data-mp="2l0h3mdic9qhf">
                    {user.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-right" data-id="element-245" data-mp="2sk8jdw781js4">
                  <button className="text-slate-400 hover:text-slate-600" data-id="element-246" data-mp="2xj33r2ku7gec">
                    <MoreVertical className="h-4 w-4" data-id="element-247" data-mp="2xl60e5ghw7s1" />
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function CompetencyManagementPanel() {
  return <div className="p-8 text-center max-w-lg mx-auto mt-12" data-id="element-248" data-mp="3bht1mkfhdk3r">
      <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-249" data-mp="3g9ayoxedv0jg">
        <Target className="h-8 w-8 text-indigo-600" data-id="element-250" data-mp="3l8e2keimn0pu" />
      </div>
      <h2 className="text-xl font-semibold text-slate-900 mb-2" data-id="element-251" data-mp="3l7m8570rn915">
        Competency Domains
      </h2>
      <p className="text-slate-500 mb-8" data-id="element-252" data-mp="3q9bj6mnznmp8">
        Each role carries a standard set of competency domains. Every domain
        offers Characteristic and Behaviour questions, from which subscribers
        choose two of each per cycle.
      </p>
      <Link to="/app/competencies" className="inline-flex items-center bg-slate-900 text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-253" data-mp="fdkrd08b3udh">
        Open Competency Domains
        <ExternalLink className="h-4 w-4 ml-2" data-id="element-254" data-mp="of0zt2fk9mf2" />
      </Link>
    </div>;
}
function QuestionManagementPanel() {
  return <div className="p-8 text-center max-w-lg mx-auto mt-12" data-id="element-255" data-mp="m2e3a6u0dxt">
      <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-256" data-mp="3ssz4hiuc5243">
        <ListChecks className="h-8 w-8 text-blue-600" data-id="element-257" data-mp="aeq8xze947tw" />
      </div>
      <h2 className="text-xl font-semibold text-slate-900 mb-2" data-id="element-258" data-mp="5nhw0tdpq4bz">
        Question Bank
      </h2>
      <p className="text-slate-500 mb-8" data-id="element-259" data-mp="3e2ry41hlr9jr">
        Every Characteristic and Behaviour question in the framework, browsable
        by role and competency domain. The first two of each type in a domain
        form its standard set.
      </p>
      <Link to="/app/questions" className="inline-flex items-center bg-slate-900 text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-260" data-mp="1ol4j4yn9d23c">
        Open Question Library
        <ExternalLink className="h-4 w-4 ml-2" data-id="element-261" data-mp="1fj50k1wlhpya" />
      </Link>
    </div>;
}
function AssessmentTemplatesPanel() {
  return <div className="flex flex-col h-full" data-id="element-262" data-mp="28a6iubvl41m1">
      <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50" data-id="element-263" data-mp="28b857ci1waj2">
        <div data-id="element-264" data-mp="2ca752ioxdh3n">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-265" data-mp="2capbu997rdpt">
            Role Questionnaires
          </h2>
          <p className="text-sm text-slate-500" data-id="element-266" data-mp="1ydtpjf3ybprt">
            Each role starts from a standard set: {PICKS_PER_TYPE} Characteristic
            and {PICKS_PER_TYPE} Behaviour questions per competency domain.
          </p>
        </div>
        <Link to="/app/competencies" className="border border-slate-200 bg-white text-slate-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors flex items-center shrink-0" data-id="element-267" data-mp="rw6ln8o4iq2h">
          View domains
          <ExternalLink className="h-4 w-4 ml-2" data-id="element-268" data-mp="1b0569ycj856y" />
        </Link>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-269" data-mp="wb8z03iauw7e">
        {ROLES.map(role => <div key={role.id} className="border border-slate-200 rounded-xl p-5 hover:border-slate-300 transition-colors" data-id="element-270" data-mp="119twvwa2x3so">
            <div className="flex justify-between items-start mb-3" data-id="element-271" data-mp="rcpca0vvtgdz">
              <div className="p-2 bg-slate-100 rounded-lg text-slate-600" data-id="element-272" data-mp="raup0fx51c6q">
                <FileSpreadsheet className="h-5 w-5" data-id="element-273" data-mp="deqwqs5trdww" />
              </div>
              <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full" data-id="element-274" data-mp="ibhf5m53qllh">
                {role.group}
              </span>
            </div>
            <h3 className="font-semibold text-slate-900 mb-1" data-id="element-275" data-mp="2cq1tlh2txkwf">{role.name}</h3>
            <div className="flex items-center text-xs text-slate-500 space-x-3" data-id="element-276" data-mp="2cnxrn4ohqvys">
              <span data-id="element-277" data-mp="13839zivx9dh">{role.domains.length} domains</span>
              <span data-id="element-278" data-mp="3sa11qvmamueb">•</span>
              <span data-id="element-279" data-mp="3saknknf2e2xm">{questionnaireSize(role)} questions (standard set)</span>
            </div>
          </div>)}
      </div>
    </div>;
}
function EmailTemplatesPanel() {
  return <div className="flex flex-col h-full" data-id="element-280" data-mp="6102leluzxrp">
      <div className="p-5 border-b border-slate-200 bg-slate-50" data-id="element-281" data-mp="606t45vimu5v">
        <h2 className="text-lg font-semibold text-slate-900" data-id="element-282" data-mp="jrv4u81urgmb">
          Email Templates
        </h2>
        <p className="text-sm text-slate-500" data-id="element-283" data-mp="ettlrn4ffhau">
          Customize automated system communications.
        </p>
      </div>
      <div className="flex-1 flex" data-id="element-284" data-mp="tpjaht0zy8j9">
        <div className="w-1/3 border-r border-slate-200 overflow-y-auto" data-id="element-285" data-mp="orzqechmb1je">
          <div className="divide-y divide-slate-100" data-id="element-286" data-mp="2viz5f244a9k1">
            {['Assessment Invitation', 'Reminder: 3 Days Left', 'Reminder: 24 Hours', 'Assessment Completed', 'Report Ready'].map((tpl, i) => <button key={i} className={`w-full text-left px-4 py-3 text-sm transition-colors ${i === 0 ? 'bg-indigo-50 text-indigo-700 font-medium border-l-2 border-indigo-600' : 'text-slate-600 hover:bg-slate-50 border-l-2 border-transparent'}`} data-id="element-287" data-mp="3ga4azdmac31f" data-mpi={i}>
                {tpl}
              </button>)}
          </div>
        </div>
        <div className="flex-1 p-6 bg-slate-50/50" data-id="element-288" data-mp="319fyrvnsu1b">
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm" data-id="element-289" data-mp="72lanbte45gd">
            <div className="p-4 border-b border-slate-200" data-id="element-290" data-mp="bu39nuburvyp">
              <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-291" data-mp="bulcj9t0t8fl">
                Subject Line
              </label>
              <input type="text" defaultValue="Action Required: 360° Leadership Assessment for {{target_name}}" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-indigo-100 outline-none" data-id="element-292" data-mp="lrzkel0voowa" />
            </div>
            <div className="p-4" data-id="element-293" data-mp="269ig5q2xjnis">
              <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-294" data-mp="2698qy4pplke5">
                Email Body
              </label>
              <textarea rows={8} defaultValue={`Hi {{respondent_name}},\n\nYou have been invited to participate in a 360° Leadership Assessment for {{target_name}}.\n\nYour feedback is crucial for their professional development. Please complete the assessment by {{due_date}}.\n\nClick the link below to begin:\n{{assessment_link}}\n\nThank you,\nThe HR Team`} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm font-mono text-slate-700 focus:ring-2 focus:ring-indigo-100 outline-none" data-id="element-295" data-mp="21d1io59ubwt1" />
            </div>
            <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end" data-id="element-296" data-mp="3crf1uwtnfo2q">
              <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center" data-id="element-297" data-mp="37xud7vdy46g7">
                <Save className="h-4 w-4 mr-2" data-id="element-298" data-mp="2xh081tqgqxnt" />
                Save Template
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function PlatformSettingsPanel() {
  return <div className="p-6 max-w-2xl" data-id="element-299" data-mp="2njcbcy9arg30">
      <h2 className="text-lg font-semibold text-slate-900 mb-6" data-id="element-300" data-mp="2nijauf0xlmtb">
        Platform Settings
      </h2>

      <div className="space-y-8" data-id="element-301" data-mp="3b9yjsqn7r2dj">
        <section data-id="element-302" data-mp="3g6owd3zt7fu7">
          <h3 className="text-sm font-medium text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-303" data-mp="3g78986alre1d">
            Branding & Appearance
          </h3>
          <div className="space-y-4" data-id="element-304" data-mp="31fzks0ifrypt">
            <div data-id="element-305" data-mp="36gn7i9zmtte8">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-306" data-mp="36g3t3396xn4y">
                Organization Name
              </label>
              <input type="text" defaultValue={ORGANISATION.name} className="w-full max-w-md px-3 py-2 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-slate-100 outline-none" data-id="element-307" data-mp="3u4n0zhobvxwi" />
            </div>
            <div data-id="element-308" data-mp="1iiuwmzmxtta2">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-309" data-mp="1iha1p8wpo8bo">
                Primary Color (Hex)
              </label>
              <div className="flex items-center space-x-3" data-id="element-310" data-mp="1s9fom483vzk0">
                <input type="text" defaultValue="#0f172a" className="w-32 px-3 py-2 border border-slate-200 rounded-md text-sm font-mono focus:ring-2 focus:ring-slate-100 outline-none" data-id="element-311" data-mp="1sbaezy25cjvh" />
                <div className="w-8 h-8 rounded-md bg-slate-900 border border-slate-200" data-id="element-312" data-mp="1dkjk2h59d4o5"></div>
              </div>
            </div>
          </div>
        </section>

        <section data-id="element-313" data-mp="2ozgbtystu7z7">
          <h3 className="text-sm font-medium text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-314" data-mp="2oyx57e8mn60h">
            Assessment Defaults
          </h3>
          <div className="space-y-4" data-id="element-315" data-mp="2kxldn3670apn">
            <div className="flex items-center justify-between max-w-md" data-id="element-316" data-mp="1hn7kakq14x7x">
              <div data-id="element-317" data-mp="1hp1t50o10yvn">
                <div className="text-sm font-medium text-slate-700" data-id="element-318" data-mp="1mm1zlcosg3na">
                  Require Minimum Respondents
                </div>
                <div className="text-xs text-slate-500" data-id="element-319" data-mp="1rm6l5h4jzokd">
                  Prevent report generation if under threshold
                </div>
              </div>
              <select className="px-3 py-1.5 border border-slate-200 rounded-md text-sm outline-none" data-id="element-320" data-mp="ywg5h3n9wem5">
                <option data-id="element-321" data-mp="13vauwofqx1f8">3 Respondents</option>
                <option data-id="element-322" data-mp="13wc4spl0zhvd">5 Respondents</option>
                <option data-id="element-323" data-mp="17x5w7sxofet0">No Minimum</option>
              </select>
            </div>
            <div className="flex items-center justify-between max-w-md" data-id="element-324" data-mp="17v1kwpxg7dip">
              <div data-id="element-325" data-mp="1cpffiq4adgmb">
                <div className="text-sm font-medium text-slate-700" data-id="element-326" data-mp="1cpz0ygt5an53">
                  Enforce Anonymity
                </div>
                <div className="text-xs text-slate-500" data-id="element-327" data-mp="k7blbgwx40be">
                  Hide individual rater names on reports
                </div>
              </div>
              <ToggleRight className="h-6 w-6 text-emerald-500" data-id="element-328" data-mp="cn9bt5jykad6" />
            </div>
          </div>
        </section>

        <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-329" data-mp="7muzvcf8kg9v">
          Save Changes
        </button>
      </div>
    </div>;
}
function AISettingsPanel() {
  return <div className="p-6 max-w-2xl" data-id="element-330" data-mp="3pv41uyk2oygk">
      <div className="flex items-center mb-6" data-id="element-331" data-mp="3pw5cx3139kya">
        <Sparkles className="h-5 w-5 text-indigo-600 mr-2" data-id="element-332" data-mp="3l445buev8iel" />
        <h2 className="text-lg font-semibold text-slate-900" data-id="element-333" data-mp="3l2jb65wgr1h9">
          AI Features Configuration
        </h2>
      </div>

      <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 mb-8 text-sm text-indigo-800" data-id="element-334" data-mp="3g2euaern84qi">
        AI features utilize OpenAI models to generate insights and summaries.
        Data is processed securely and is not used to train public models.
      </div>

      <div className="space-y-6" data-id="element-335" data-mp="325rtu20hewfu">
        {[{
        title: 'Executive Summaries',
        desc: 'Auto-generate narrative summaries for completed assessments.',
        active: true
      }, {
        title: 'Blind Spot Detection',
        desc: 'Flag significant discrepancies between self and peer ratings.',
        active: true
      }, {
        title: 'Coaching Recommendations',
        desc: 'Suggest actionable coaching topics based on assessment results.',
        active: true
      }, {
        title: 'Sentiment Analysis',
        desc: 'Analyze open-text responses for overall sentiment trends.',
        active: false
      }].map((feature, i) => <div key={i} className="flex items-start justify-between border-b border-slate-100 pb-4 last:border-0" data-id="element-336" data-mp="67ikjkuyqnjd" data-mpi={i}>
            <div data-id="element-337" data-mp="3tb5jrt9ofhn8">
              <div className="text-sm font-medium text-slate-900" data-id="element-338" data-mp="3tboqedtvmjly">
                {feature.title}
              </div>
              <div className="text-sm text-slate-500 mt-0.5" data-id="element-339" data-mp="39qerges75qs6">
                {feature.desc}
              </div>
            </div>
            {feature.active ? <ToggleRight className="h-6 w-6 text-indigo-600 shrink-0 ml-4 cursor-pointer" data-id="element-340" data-mp="3jnjiarwzmafb" /> : <ToggleLeft className="h-6 w-6 text-slate-300 shrink-0 ml-4 cursor-pointer" data-id="element-341" data-mp="3eo6rjw2zwj7g" />}
          </div>)}
      </div>
    </div>;
}
function AuditLogsPanel() {
  return <div className="flex flex-col h-full" data-id="element-342" data-mp="3npy0rawqv862">
      <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50" data-id="element-343" data-mp="3nov6qu44kplx">
        <div data-id="element-344" data-mp="6850hw00keid">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-345" data-mp="6azhtenqdj7k">Audit Logs</h2>
          <p className="text-sm text-slate-500" data-id="element-346" data-mp="1abup42mhmim">
            System-wide activity and security events.
          </p>
        </div>
        <button className="border border-slate-200 text-slate-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors bg-white" data-id="element-347" data-mp="f78i0vjj8lur">
          Export CSV
        </button>
      </div>
      <div className="flex-1 overflow-auto" data-id="element-348" data-mp="irmyzjlr7wuf">
        <table className="w-full text-sm text-left" data-id="element-349" data-mp="iqldel743ryg">
          <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-350" data-mp="wrfk8zeihkv0">
            <tr data-id="element-351" data-mp="rsbb28rl7n4o">
              <th className="px-6 py-3 font-medium" data-id="element-352" data-mp="rrta4ytzckqf">Timestamp</th>
              <th className="px-6 py-3 font-medium" data-id="element-353" data-mp="rqi5kwspn8y9">User</th>
              <th className="px-6 py-3 font-medium" data-id="element-354" data-mp="43jt0a7pol5m">Action</th>
              <th className="px-6 py-3 font-medium" data-id="element-355" data-mp="1ex14tsjcft">Resource</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-356" data-mp="dsa369cwnffp">
            {[{
            time: 'Oct 24, 14:32',
            user: CURRENT_ADMIN.name,
            action: 'Generated Report',
            resource: '2026 Executive Summary'
          }, {
            time: 'Oct 24, 11:15',
            user: 'System',
            action: 'Sent Reminders',
            resource: '2026 Board & Executive Review (12)'
          }, {
            time: 'Oct 23, 09:45',
            user: CURRENT_ADMIN.name,
            action: 'Created Assessment',
            resource: '2026 Board Effectiveness Review'
          }, {
            time: 'Oct 22, 16:20',
            user: personById('p1').name,
            action: 'Approved Rater Nominations',
            resource: 'Chief Executive Officer review'
          }, {
            time: 'Oct 22, 10:05',
            user: CURRENT_ADMIN.name,
            action: 'Imported People',
            resource: 'Board & ExCo roster.csv'
          }].map((log, i) => <tr key={i} className="hover:bg-slate-50 text-slate-600" data-id="element-357" data-mp="qb5o87llcmbg" data-mpi={i}>
                <td className="px-6 py-4 whitespace-nowrap text-slate-500" data-id="element-358" data-mp="qd8nli8mvrsu">
                  {log.time}
                </td>
                <td className="px-6 py-4 font-medium text-slate-900" data-id="element-359" data-mp="b84h44au7vvc">
                  {log.user}
                </td>
                <td className="px-6 py-4" data-id="element-360" data-mp="760x2ra3riyg">{log.action}</td>
                <td className="px-6 py-4" data-id="element-361" data-mp="78na36friqo2">{log.resource}</td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}